import {useCallback, useEffect, useReducer} from 'react';
import {PatchFilter, PatchSavedSearch} from '../index';
import {Column, Form} from '../../../../../../stores/Form';
import {createNewSearch, patchSavedSearch, prepareSearchToStore} from '../utils';
import {saveAndSearchAction, saveRecentSearches, searchAction} from '../../../../actions/searches';
import {getRecentSearchesAction} from '../../../../actions/form';
import {SavedSearch} from '../../../../types';
import {findAvailableIndex} from '../../../../actions/utils/filter';
import {initialState, AdvancedSearchDrawerReducerType, reducer} from './advancedSearch.reducer';
import {allowAISearch} from '../../../../../../_legacy/Searches/components/AISearch/utils';
import {showFormClientSideMessage} from '../../../../actions/messages';
import {priority} from '@priority-software/priority-proto-api/lib';
import {texts} from '../texts';

type Props = {
    form: Form;
    onClose: () => void;
    editSearch: PatchSavedSearch | null;
};

const initHookState = (editSearch: PatchSavedSearch | null, form: Form) => {
    const displayAISection = allowAISearch();
    return {
        ...initialState,
        showFilterFieldDropdown: editSearch.filters.length === 0,
        isAISearchAllowed: displayAISection,
        search: editSearch
    };
};

export const useAdvancedSearch = (props: Props) => {
    const {form, onClose, editSearch} = props;
    const {savedSearches} = form.searchStore;

    const [state, dispatch] = useReducer<AdvancedSearchDrawerReducerType>(
        reducer,
        initHookState(editSearch, form)
    );
    const {search} = state;

    const ignoreCase = search?.ignoreCase || false;
    const searchIsEmpty = !search || search.filters.length === 0;

    useEffect(() => {
        getRecentSearchesAction(form);
    }, [form]);

    const updateTitle = (name: string) => {
        dispatch({type: 'UPDATE_SEARCH', payload: {name}});
    };

    const setShowFilterInput = useCallback((flag: boolean) => {
        dispatch({type: 'STATE_UPDATE', payload: {showFilterFieldDropdown: flag}});
    }, []);

    const handleReorder = useCallback((filters: PatchFilter[]) => {
        dispatch({type: 'SET_FILTERS', payload: {filters}});
    }, []);

    const saveAndSearch = useCallback(
        async (search: PatchSavedSearch) => {
            const {savedSearches} = form.searchStore;
            const hasDefault = savedSearches.some(s => s.isDefault);

            const onSaveAndSearch = async (saveAsDefault?: boolean) => {
                if (saveAsDefault) {
                    // we only save as default if there is no default search
                    search.isDefault = true;
                }

                const storeSearch = prepareSearchToStore(search);
                if (storeSearch) {
                    dispatch({type: 'STATE_UPDATE', payload: {search: null}});
                    onClose();

                    await saveAndSearchAction(storeSearch, form);
                }
            };

            /**
             * We are using a promise to wait for the user to confirm the action,
             * and then we resolve the promise with the result. + We should handle both cases when the user
             * close dialog by X button or by clicking outside the dialog.
             */
            const saveAsDefault = hasDefault
                ? false
                : await new Promise<boolean>(resolve =>
                      showFormClientSideMessage(
                          form.session,
                          priority.netitems.shared.Message.Type.Warning,
                          {
                              content: texts.saveAsDefaultMessage.replace('[X]', search.name),
                              onConfirm: async () => resolve(true),
                              onClose: () => resolve(false),
                              onCancel: () => resolve(false),
                              cancleButtonText: texts.noLabel
                          }
                      )
                  );
            onSaveAndSearch(saveAsDefault);
        },
        [form, onClose]
    );

    const handleExecuteSearch = useCallback(
        (search: SavedSearch) => {
            dispatch({
                type: 'STATE_UPDATE',
                payload: {
                    search: patchSavedSearch(search, form),
                    showFilterFieldDropdown: false
                }
            });
        },
        [form]
    );

    const handleUpdateFilter = useCallback((filter: PatchFilter, columnId: number) => {
        dispatch({type: 'UPDATE_FILTER', payload: {filter, columnId}});
    }, []);

    const handleRemoveFilter = useCallback(
        (columnId: number) => dispatch({type: 'REMOVE_FILTER', payload: {columnId}}),
        []
    );

    const onSearch = useCallback(async () => {
        const storeSearch = prepareSearchToStore(search);

        if (storeSearch) {
            await searchAction(storeSearch, form);
            await saveRecentSearches(form);

            dispatch({type: 'STATE_UPDATE', payload: {search: null}});
            onClose();
        }
    }, [form, search, onClose]);

    const clearSearch = useCallback(() => {
        dispatch({
            type: 'UPDATE_SEARCH',
            payload: createNewSearch(null, findAvailableIndex(savedSearches))
        });
    }, [savedSearches]);

    const closeSearch = useCallback(() => {
        dispatch({type: 'STATE_UPDATE', payload: {search: null}});
        onClose();
    }, [onClose]);

    const handleIgnoreCase = useCallback(() => {
        dispatch({type: 'UPDATE_IGNORE_CASE', payload: {}});
    }, []);

    const onAddFilter = useCallback(
        (column: Column) => {
            dispatch({
                type: 'ADD_FILTER',
                payload: {column}
            });
        },
        [searchIsEmpty]
    );

    const setFilters = useCallback((filters: PatchFilter[]) => {
        dispatch({type: 'SET_FILTERS', payload: {filters}});
    }, []);

    return {
        state: {
            ...state,
            ignoreCase
        },
        handlers: {
            setShowFilterInput,
            onSearch,
            saveAndSearch,
            handleExecuteSearch,
            handleReorder,
            handleUpdateFilter,
            handleRemoveFilter,
            clearSearch,
            closeSearch,
            handleIgnoreCase,
            onAddFilter,
            setFilters,
            updateTitle
        }
    };
};
